import { Component } from '@theme/component';

/**
 * @typedef {object} SmsSignupDetail
 * @property {string} phone - Number normalized to E.164, for example `+15551234567`
 * @property {string} rawPhone - Number exactly as the visitor typed it
 * @property {(result: Promise<unknown> | unknown) => void} respond - Hand back the provider request;
 *   the form shows success when it resolves and an error when it rejects
 */

/**
 * Phone-number signup for SMS drop alerts.
 *
 * The form is provider-agnostic. On a valid submit it dispatches a bubbling, cancelable
 * `sms-signup:submit` event. An SMS provider integration subscribes to that event and calls
 * `event.detail.respond(promise)` with its signup request. Until an integration responds,
 * visitors see a "not open yet" message rather than a false confirmation.
 *
 * @example
 * document.addEventListener('sms-signup:submit', (event) => {
 *   event.detail.respond(provider.subscribe({ phone: event.detail.phone }));
 * });
 *
 * @typedef {object} SmsSignupRefs
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} input
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} message - Single live region for validation and result messages
 *
 * @extends {Component<SmsSignupRefs>}
 */
class SmsSignupComponent extends Component {
  requiredRefs = ['form', 'input', 'button', 'message'];

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { form, input, button } = this.refs;
    const phone = normalizePhone(input.value);

    if (!phone) {
      input.setAttribute('aria-invalid', 'true');
      this.#showMessage(this.dataset.messageInvalid);
      input.focus();
      return;
    }

    input.removeAttribute('aria-invalid');

    /** @type {Promise<unknown> | undefined} */
    let pending;

    this.dispatchEvent(
      new CustomEvent('sms-signup:submit', {
        bubbles: true,
        cancelable: true,
        detail: /** @type {SmsSignupDetail} */ ({
          phone,
          rawPhone: input.value,
          respond: (result) => {
            pending = Promise.resolve(result);
          },
        }),
      })
    );

    if (!pending) {
      console.warn('[sms-signup] No SMS provider is listening for "sms-signup:submit"; the number was not saved.');
      this.#showMessage(this.dataset.messageUnavailable);
      return;
    }

    button.disabled = true;
    this.setAttribute('aria-busy', 'true');

    try {
      await pending;
      form.reset();
      this.#showMessage(this.dataset.messageSuccess);
    } catch (error) {
      console.error('[sms-signup] Signup failed', error);

      // Providers throw an error named InvalidPhoneError when the number itself was refused.
      if (error instanceof Error && error.name === 'InvalidPhoneError') {
        input.setAttribute('aria-invalid', 'true');
        this.#showMessage(this.dataset.messageInvalid);
        input.focus();
      } else {
        this.#showMessage(this.dataset.messageError);
      }
    } finally {
      button.disabled = false;
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string | undefined} text
   */
  #showMessage(text = '') {
    this.refs.message.textContent = text;
  }
}

/**
 * Normalizes a typed phone number to E.164. Numbers without a country code are treated as
 * US/Canada (+1).
 *
 * @param {string} value
 * @returns {string | null} The E.164 number, or `null` when the input is not a plausible number
 */
export function normalizePhone(value) {
  const trimmed = value.trim();
  if (!/^[+\d\s().-]+$/.test(trimmed)) return null;

  const digits = trimmed.replace(/\D/g, '');

  if (trimmed.startsWith('+')) {
    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
  }

  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

  return null;
}

if (!customElements.get('sms-signup-component')) {
  customElements.define('sms-signup-component', SmsSignupComponent);
}
